import * as yup from 'yup';
import {
  type EVBServiceCompany,
  vuInformationSchema,
} from '@/features/EVBService/types';
import { useInputStore } from '@/features/Sparten';
import { clientSchema, NO_SALUTATION_SELECTED } from '@/features/Clients';
import { getDay100YearsBefore, getDayNYearsBefore } from '@/utils/dates/dates';
import { isCompany } from '@/features/Sparten/shared/renderConditions/kfz/sharedRenderConditions';
import { KASKOTYP } from '@/features/Sparten/shared/types';
import {
  isAbweichenderHalterVertretungsberechtigterRequired,
  isVertretungsberechtigterRequired,
} from '@/features/Sparten/shared/sharedRenderConditions';
import { useEVBInputStore } from '@/features/EVBService/useEVBInputStore';

export const eVBInputSchema = yup.object({
  hasAbweichenderHalter: yup.boolean().default(false),
  company: vuInformationSchema.default(null).required(),
  requiresCreditAssessment: yup.boolean().default(false).required(),
  allowsCreditAssessment: yup
    .boolean()
    .default(false)
    .when('requiresCreditAssessment', {
      is: (val: boolean) => val,
      then: yup.boolean().isTrue(),
    }),
  deckungsTyp: yup
    .string()
    .oneOf([KASKOTYP.HAFTPFLICHT, KASKOTYP.TEILKASKO, KASKOTYP.VOLLKASKO])
    .default(KASKOTYP.HAFTPFLICHT)
    .required(),
  deckung: yup
    .number()
    .default(null)
    .nullable()
    .when(['company', 'deckungsTyp'], {
      is: (company: EVBServiceCompany | null, typ: string) => {
        if (typ === KASKOTYP.HAFTPFLICHT) return false;

        return company?.allowDeckungen ?? false;
      },
      then: yup.number().required(),
    }),
  kennzeichen: yup.object({
    typ: yup.number().default(2).required(),
    betriebszeitraumVon: yup.number().default(3).max(12).required(),
    betriebszeitraumBis: yup.number().default(10).max(12).required(),
  }),
  fahrzeugArt: yup.number().default(2).required(),
  versicherungsbeginnTyp: yup
    .string()
    .oneOf(['tag', 'date'])
    .default('tag')
    .required(),
  versicherungsbeginn: yup
    .date()
    .nullable()
    .default(null)
    .when('versicherungsbeginnTyp', {
      is: (val: string) => val === 'date',
      then: yup.date().required(),
    }),
  abweichenderHalter: yup.object({
    isFirma: yup.boolean().default(false),
    firma: yup
      .string()
      .default('')
      .when('isFirma', {
        is: (isFirma: boolean) => isFirma,
        then: yup.string().required(),
      }),
  }),
  hsn: yup
    .string()
    .default('')
    .when(['company'], {
      is: (company: EVBServiceCompany | null) => {
        return company?.requireHSN ?? false;
      },
      then: yup.string().required(),
    }),
  tsn: yup
    .string()
    .default('')
    .when(['company'], {
      is: (company: EVBServiceCompany | null) => {
        return company?.requireTSN ?? false;
      },
      then: yup.string().required(),
    }),
});

export const eVBAbweichenderHalterEntitySchema = yup.object({
  anredeId: yup
    .number()
    .default(NO_SALUTATION_SELECTED)
    .when({
      is: () =>
        useEVBInputStore().hasAbweichenderHalter &&
        useEVBInputStore().abweichenderHalter.isFirma === false,
      then: yup.number().required().notOneOf([NO_SALUTATION_SELECTED]),
    }),
  vorname: yup
    .string()
    .default('')
    .when({
      is: () =>
        useEVBInputStore().hasAbweichenderHalter &&
        (useEVBInputStore().abweichenderHalter.isFirma === false ||
          isAbweichenderHalterVertretungsberechtigterRequired.value),
      then: yup.string().required(),
    }),
  nachname: yup
    .string()
    .default('')
    .when({
      is: () =>
        useEVBInputStore().hasAbweichenderHalter &&
        (useEVBInputStore().abweichenderHalter.isFirma === false ||
          isAbweichenderHalterVertretungsberechtigterRequired.value),
      then: yup.string().required(),
    }),
  geburtsdatum: yup
    .date()
    .nullable()
    .default(null)
    .when({
      is: () =>
        useEVBInputStore().hasAbweichenderHalter &&
        useEVBInputStore().abweichenderHalter.isFirma === false,
      then: yup.date().required(),
    }),
  adresse: yup.object({
    strasse: yup
      .string()
      .default('')
      .when({
        is: () =>
          useEVBInputStore().hasAbweichenderHalter ||
          useEVBInputStore().abweichenderHalter.isFirma === true,
        then: yup.string().required(),
      }),
    hausnummer: yup
      .string()
      .default('')
      .when({
        is: () =>
          useEVBInputStore().hasAbweichenderHalter ||
          useEVBInputStore().abweichenderHalter.isFirma === true,
        then: yup.string().required(),
      }),
    plz: yup
      .string()
      .default('')
      .max(5, 'Bitte geben Sie eine gültige Postleitzahl ein')
      .when({
        is: () =>
          useEVBInputStore().hasAbweichenderHalter ||
          useEVBInputStore().abweichenderHalter.isFirma === true,
        then: yup.string().required(),
      }),
    ort: yup
      .string()
      .default('')
      .when({
        is: () =>
          useEVBInputStore().hasAbweichenderHalter ||
          useEVBInputStore().abweichenderHalter.isFirma === true,
        then: yup.string().required(),
      }),
  }),
});

export const vnEvbModal = clientSchema.concat(
  yup.object({
    anredeId: yup
      .number()
      .default(NO_SALUTATION_SELECTED)
      .when({
        is: () => !isCompany.value,
        then: yup.number().required().notOneOf([NO_SALUTATION_SELECTED]),
      }),
    vorname: yup
      .string()
      .default('')
      .when({
        is: () => isVertretungsberechtigterRequired.value,
        then: yup.string().required(),
      }),
    nachname: yup
      .string()
      .default('')
      .when({
        is: () => isVertretungsberechtigterRequired.value,
        then: yup.string().required(),
      }),
    geburtsdatum: yup
      .date()
      .nullable()
      .default(null)
      .when({
        is: () => useInputStore().versicherungsnehmer.isFirma === false,
        then: yup
          .date()
          .min(getDay100YearsBefore())
          .max(getDayNYearsBefore(17))
          .required(),
      }),
    adresse: yup.object({
      strasse: yup.string().default('').required(),
      hausnummer: yup.string().default('').required(),
      ort: yup.string().default('').required(),
      plz: yup
        .string()
        .default('')
        .max(5, 'Bitte geben Sie eine gültige Postleitzahl ein')
        .required(),
    }),
  }),
);
